import { AlertFieldsFragment } from '@api/gql/graphql';
import { CloseButton, LabeledTextField, MaterialIcon } from '@components';
import { useGlobalNotifier } from '@hooks';
import { Box, Button, IconButton, Popover, Stack, styled, Typography } from '@mui/material';
import { CustomAnalyticsEvents, useAnalyticsContext } from '@utils/analytics';
import { useState } from 'react';

const PopoverContentWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  width: '500px',
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  width: '32px',
  height: '32px',
  '&.positive': {
    backgroundColor: theme.palette.success.main,
  },
  '&.negative': {
    backgroundColor: theme.palette.error.main,
  },
}));

export function AlertFeedbackNegativeModal({ alert }: { alert: AlertFieldsFragment }) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { track } = useAnalyticsContext();

  const { notifySuccess } = useGlobalNotifier();

  const [reason, setReason] = useState('');

  const handleClose = (didSubmit: boolean) => {
    track(CustomAnalyticsEvents.ACCESS_ANOMALIES_POSITIVE_FEEDBACK, { reason, alert: alert.type });
    if (didSubmit) {
      notifySuccess('Thank you for your feedback');
    }
    setAnchorEl(null);

    setReason('');
  };

  const open = Boolean(anchorEl);

  const id = open ? `${alert.id}-negative-feedback-popover` : undefined;

  const modalText = 'Why was it not helpful?';

  return (
    <>
      <StyledIconButton
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)}
        className="negative"
      >
        <MaterialIcon symbol={'thumb_down_off_alt_icon'} weight={800} size="small" />
      </StyledIconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={() => handleClose(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <PopoverContentWrapper>
          <Stack direction="row" spacing={2} justifyContent={'space-between'}>
            <Typography variant="subtitle1">{modalText}</Typography>
            <CloseButton onClose={() => handleClose(false)} />
          </Stack>
          <LabeledTextField
            label="Reason"
            value={reason}
            maxRows={3}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={3}
          />
          <Button
            onClick={() => handleClose(true)}
            variant="contained"
            sx={{ width: '100px', display: 'flex', flexDirection: 'row', alignSelf: 'flex-end' }}
            color="primary"
          >
            Submit
          </Button>
        </PopoverContentWrapper>
      </Popover>
    </>
  );
}

export function AlertFeedbackPositiveButton({ alert }: { alert: AlertFieldsFragment }) {
  const { track } = useAnalyticsContext();

  const { notifySuccess } = useGlobalNotifier();

  const handleClick = () => {
    track(CustomAnalyticsEvents.ACCESS_ANOMALIES_POSITIVE_FEEDBACK, { alert: alert.type });
    notifySuccess('Thank you for your feedback');
  };

  return (
    <StyledIconButton className="positive" onClick={handleClick}>
      <MaterialIcon symbol={'thumb_up_off_alt_icon'} weight={800} size="small" />
    </StyledIconButton>
  );
}
